import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { RegistrationAttachmentTypeActions } from './registration-attachment-types.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
    DELETE_REGISTRATION_ATTACHMENT_TYPE,
    GET_ATTACHMENT_TYPES_BY_REGISTRATION_TYPE,
    SAVE_REGISTRATION_ATTACHMENT_TYPE
} from '@modules/settings/settings.graphql';
import {
    GetAttachmentTypesByRegistrationTypeResponse,
    SaveRegistrationAttachmentTypeResponse
} from '@modules/settings/types/registration-attachment-type';

@Injectable()
export class RegistrationAttachmentTypeEffects {
    private readonly actions$ = inject(Actions);
    private readonly graphqlService = inject(GraphQlService);
    private readonly swalService = inject(SwalService);

    loadByRegistrationType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RegistrationAttachmentTypeActions.loadByRegistrationType),
            mergeMap((action) =>
                this.graphqlService.query<GetAttachmentTypesByRegistrationTypeResponse>({
                    query: GET_ATTACHMENT_TYPES_BY_REGISTRATION_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { registrationType: action.registrationType }
                }).pipe(
                    map((response) => RegistrationAttachmentTypeActions.loadByRegistrationTypeSuccess({
                        items: response.data?.getAttachmentTypesByRegistrationType ?? []
                    })),
                    catchError((error) => of(RegistrationAttachmentTypeActions.loadByRegistrationTypeFailure({
                        error: error.message
                    })))
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RegistrationAttachmentTypeActions.create),
            mergeMap((action) =>
                this.graphqlService.mutate<{ saveRegistrationAttachmentType: SaveRegistrationAttachmentTypeResponse }>({
                    mutation: SAVE_REGISTRATION_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { input: action.input }
                }).pipe(
                    map((response) => {
                        const res = response.data!.saveRegistrationAttachmentType;
                        if (res.status) {
                            return RegistrationAttachmentTypeActions.createSuccess({ item: res.data });
                        } else {
                            return RegistrationAttachmentTypeActions.createFailure({ error: res.message });
                        }
                    }),
                    catchError((error) => of(RegistrationAttachmentTypeActions.createFailure({
                        error: error.message
                    })))
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RegistrationAttachmentTypeActions.update),
            mergeMap((action) =>
                this.graphqlService.mutate<{ saveRegistrationAttachmentType: SaveRegistrationAttachmentTypeResponse }>({
                    mutation: SAVE_REGISTRATION_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    // Pass input directly. If backend needs UUID in input, we assume action.input has it or we merge it.
                    // Based on my previous form implementation, I passed uuid in input for edit.
                    // The action payload has uuid separate but input interface doesn't enforce it.
                    // I will reconstruct the input to include uuid.
                    variables: { input: { ...action.input, uuid: action.uuid } }
                }).pipe(
                    map((response) => {
                        const res = response.data!.saveRegistrationAttachmentType;
                        if (res.status) {
                            return RegistrationAttachmentTypeActions.updateSuccess({ item: res.data });
                        } else {
                            return RegistrationAttachmentTypeActions.updateFailure({ error: res.message });
                        }
                    }),
                    catchError((error) => of(RegistrationAttachmentTypeActions.updateFailure({
                        error: error.message
                    })))
                )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RegistrationAttachmentTypeActions.delete),
            mergeMap((action) =>
                this.graphqlService.mutate<{ deleteRegistrationAttachmentType: { status: boolean, message: string } }>({
                    mutation: DELETE_REGISTRATION_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { uuid: action.uuid }
                }).pipe(
                    map((response) => {
                        const res = response.data!.deleteRegistrationAttachmentType;
                        if (res.status) {
                            return RegistrationAttachmentTypeActions.deleteSuccess({ uuid: action.uuid });
                        } else {
                            return RegistrationAttachmentTypeActions.deleteFailure({ error: res.message });
                        }
                    }),
                    catchError((error) => of(RegistrationAttachmentTypeActions.deleteFailure({
                        error: error.message
                    })))
                )
            )
        )
    );

    notifications$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    RegistrationAttachmentTypeActions.createSuccess,
                    RegistrationAttachmentTypeActions.updateSuccess,
                    RegistrationAttachmentTypeActions.deleteSuccess,
                    RegistrationAttachmentTypeActions.createFailure,
                    RegistrationAttachmentTypeActions.updateFailure,
                    RegistrationAttachmentTypeActions.deleteFailure,
                    RegistrationAttachmentTypeActions.loadByRegistrationTypeFailure
                ),
                tap((action) => {
                    if (action.type === RegistrationAttachmentTypeActions.createSuccess.type) {
                        this.swalService.successToast('Registration Attachment Created Successfully');
                    } else if (action.type === RegistrationAttachmentTypeActions.updateSuccess.type) {
                        this.swalService.successToast('Registration Attachment Updated Successfully');
                    } else if (action.type === RegistrationAttachmentTypeActions.deleteSuccess.type) {
                        this.swalService.successToast('Registration Attachment Deleted Successfully');
                    } else if (action.type === RegistrationAttachmentTypeActions.createFailure.type ||
                        action.type === RegistrationAttachmentTypeActions.updateFailure.type ||
                        action.type === RegistrationAttachmentTypeActions.deleteFailure.type ||
                        action.type === RegistrationAttachmentTypeActions.loadByRegistrationTypeFailure.type) {
                        this.swalService.errorToast((action as any).error);
                    }
                })
            ),
        { dispatch: false }
    );
}
